import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { calcProfit } from '../../lib/calcProfit';

const FUEL_MPG = 7;
const FUEL_PRICE = 5.50;

interface Props {
  load: any;
  driver: any | null;
  companyId: string | null;
}

interface Analysis {
  decision: 'ACCEPT' | 'COUNTER' | 'WALK';
  offered: number;
  offeredRpm: number;
  floorRate: number; floorRpm: number;
  targetRate: number; targetRpm: number;
  stretchRate: number; stretchRpm: number;
  fuelCost: number; driverPay: number;
  laneHistory: any[];
  laneAvgRpm: number | null;
  datBenchmark: any | null;
  talkingPoints: string[];
  counterOffer: number;
  counterRpm: number;
  loading: boolean;
  error: string | null;
}

function calcTiers(load: any, driver: any | null) {
  const miles = Number(load.loaded_miles || 0);
  const dhmiles = Number(load.deadhead_miles || 0);
  const totalMiles = miles + dhmiles;
  const fuelCost = (totalMiles / FUEL_MPG) * FUEL_PRICE;
  const rate = Number(load.rate_total || 0);
  const payRate = driver?.pay_rate_percent || driver?.pay_percent || 25;
  const payPerMile = driver?.pay_rate_per_mile;
  const driverPay = payPerMile ? payPerMile * miles : rate * (payRate / 100);
  const totalCost = fuelCost + driverPay;
  const floorRate = totalCost * 1.10;
  const targetRate = totalCost * 1.25;
  const floorRpm = miles > 0 ? floorRate / miles : 0;
  const targetRpm = miles > 0 ? targetRate / miles : 0;
  const offeredRpm = miles > 0 ? rate / miles : 0;
  return { miles, fuelCost, driverPay, totalCost, floorRate, targetRate, floorRpm, targetRpm, offeredRpm };
}

const decisionColor = (d: string) => d === 'ACCEPT' ? '#32CD32' : d === 'COUNTER' ? '#FFD700' : '#ff4444';
const decisionLabel = (d: string) => d === 'ACCEPT' ? '✓ ACCEPT' : d === 'COUNTER' ? '↕ COUNTER' : '✗ WALK';

export default function RateNegotiatorPanel({ load, driver, companyId }: Props) {
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [expanded, setExpanded] = useState(true);

  const runAnalysis = useCallback(async () => {
    if (!load?.pickup_city || !load?.delivery_city || !load?.loaded_miles) return;

    setAnalysis(prev => ({ ...(prev as any), loading: true, error: null }));

    const tiers = calcTiers(load, driver);
    const offered = Number(load.rate_total || 0);

    // Lane history from Supabase
    let laneHistory: any[] = [];
    let laneAvgRpm: number | null = null;
    if (companyId && load.pickup_state && load.delivery_state) {
      const { data: hist } = await supabase.from('loads')
        .select('load_number,rate_total,loaded_miles,rpm,profit_margin_percent,delivery_date,customer_name')
        .eq('company_id', companyId)
        .eq('pickup_state', load.pickup_state)
        .ilike('pickup_city', `%${load.pickup_city}%`)
        .eq('delivery_state', load.delivery_state)
        .ilike('delivery_city', `%${load.delivery_city}%`)
        .eq('status', 'DELIVERED')
        .neq('id', load.id)
        .order('delivery_date', { ascending: false })
        .limit(10);
      laneHistory = hist || [];
      if (laneHistory.length > 0) {
        laneAvgRpm = laneHistory.reduce((s, l) => s + Number(l.rpm || 0), 0) / laneHistory.length;
      }
    }

    // DAT benchmark from cache
    let datBenchmark: any = null;
    if (companyId) {
      const { data: bench } = await supabase.from('rate_benchmarks')
        .select('*')
        .eq('origin_city', load.pickup_city).eq('origin_state', load.pickup_state)
        .eq('destination_city', load.delivery_city).eq('destination_state', load.delivery_state)
        .gte('expires_at', new Date().toISOString())
        .maybeSingle();
      datBenchmark = bench;
    }

    const stretchRate = Math.max(
      laneHistory.length > 0 ? Math.max(...laneHistory.map(l => Number(l.rate_total || 0))) : 0,
      datBenchmark ? Number(datBenchmark.dat_high_rpm || 0) * tiers.miles : 0,
      tiers.targetRate * 1.15
    );

    let decision: 'ACCEPT' | 'COUNTER' | 'WALK';
    if (offered >= tiers.targetRate) decision = 'ACCEPT';
    else if (offered >= tiers.floorRate) decision = 'COUNTER';
    else decision = 'WALK';

    // Talking points — generated locally (NestJS/Claude used server-side; fallback here)
    const laneRef = laneAvgRpm ? `I've been averaging $${laneAvgRpm.toFixed(2)}/mi on this lane` : null;
    const datRef = datBenchmark ? `DAT is showing $${datBenchmark.dat_avg_rpm}/mi market average right now` : null;
    const talkingPoints = [
      `My floor on a ${tiers.miles.toLocaleString()}-mile haul like this is $${tiers.floorRate.toFixed(0)} — that covers fuel and driver pay with nothing left for the truck.`,
      laneRef || `The target on this lane is $${tiers.targetRpm.toFixed(2)}/mi. I need at least $${tiers.targetRate.toFixed(0)} to make it work.`,
      datRef || `I've got operating costs of $${tiers.fuelCost.toFixed(0)} in fuel and $${tiers.driverPay.toFixed(0)} in driver pay before I see a dollar.`,
    ].filter(Boolean) as string[];

    const newAnalysis: Analysis = {
      decision,
      offered,
      offeredRpm: tiers.offeredRpm,
      floorRate: tiers.floorRate, floorRpm: tiers.floorRpm,
      targetRate: tiers.targetRate, targetRpm: tiers.targetRpm,
      stretchRate, stretchRpm: tiers.miles > 0 ? stretchRate / tiers.miles : 0,
      fuelCost: tiers.fuelCost, driverPay: tiers.driverPay,
      laneHistory, laneAvgRpm, datBenchmark,
      talkingPoints,
      counterOffer: tiers.targetRate,
      counterRpm: tiers.targetRpm,
      loading: false, error: null,
    };

    setAnalysis(newAnalysis);

    // Save floor/target to load record
    if (load.id) {
      await supabase.from('loads').update({
        floor_rate: tiers.floorRate,
        target_rate: tiers.targetRate,
        rate_accepted_vs_target: offered >= tiers.targetRate ? 'ABOVE' : offered >= tiers.floorRate ? 'AT' : 'BELOW',
      }).eq('id', load.id);
    }
  }, [load, driver, companyId]);

  useEffect(() => {
    if (load?.pickup_city && load?.delivery_city && load?.loaded_miles) {
      runAnalysis();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load?.id, load?.pickup_city, load?.delivery_city, load?.loaded_miles, load?.rate_total]);

  if (!load?.pickup_city || !load?.delivery_city || !load?.loaded_miles) return null;

  const panelStyle: React.CSSProperties = { border:'1px solid #1e3a1e', background:'#050f05', marginBottom:'16px', fontFamily:'monospace' };
  const headerStyle: React.CSSProperties = { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 16px', borderBottom:'1px solid #1e3a1e', cursor:'pointer' };
  const sectionLabel: React.CSSProperties = { color:'#32CD32', fontSize:'9px', letterSpacing:'2px', fontWeight:700, marginBottom:'8px' };
  const kpi: React.CSSProperties = { background:'#0a0a0a', border:'1px solid #1a1a1a', padding:'10px 12px' };

  if (!analysis || analysis.loading) {
    return (
      <div style={panelStyle}>
        <div style={headerStyle}>
          <span style={{ color:'#32CD32', fontSize:'10px', fontWeight:700, letterSpacing:'2px' }}>⚡ JAKE RATE ADVISOR</span>
          <span style={{ color:'#555', fontSize:'11px' }}>Analyzing lane...</span>
        </div>
      </div>
    );
  }

  const dc = decisionColor(analysis.decision);

  return (
    <div style={panelStyle}>
      {/* Header */}
      <div style={headerStyle} onClick={()=>setExpanded(!expanded)}>
        <span style={{ color:'#32CD32', fontSize:'10px', fontWeight:700, letterSpacing:'2px' }}>⚡ JAKE RATE ADVISOR — {load.pickup_city}, {load.pickup_state} → {load.delivery_city}, {load.delivery_state} ({load.loaded_miles} mi)</span>
        <div style={{ display:'flex', gap:'12px', alignItems:'center' }}>
          <span style={{ color:dc, fontWeight:900, fontSize:'13px', border:`1px solid ${dc}`, padding:'3px 12px', letterSpacing:'1px' }}>{decisionLabel(analysis.decision)}</span>
          <span style={{ color:'#555', fontSize:'11px' }}>{expanded?'▲':'▼'}</span>
        </div>
      </div>

      {expanded && (
        <div style={{ padding:'16px' }}>
          {/* Rate Tiers */}
          <div style={sectionLabel}>RATE TIERS</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:'8px', marginBottom:'16px' }}>
            {[
              { label:'OFFERED', val:`$${analysis.offered.toFixed(0)}`, rpm:`$${analysis.offeredRpm.toFixed(2)}/mi`, color:analysis.offered<analysis.floorRate?'#ff4444':analysis.offered<analysis.targetRate?'#FFD700':'#32CD32' },
              { label:'FLOOR (min)', val:`$${analysis.floorRate.toFixed(0)}`, rpm:`$${analysis.floorRpm.toFixed(2)}/mi`, color:'#ff4444' },
              { label:'TARGET', val:`$${analysis.targetRate.toFixed(0)}`, rpm:`$${analysis.targetRpm.toFixed(2)}/mi`, color:'#FFD700' },
              { label:'STRETCH', val:`$${analysis.stretchRate.toFixed(0)}`, rpm:`$${analysis.stretchRpm.toFixed(2)}/mi`, color:'#32CD32' },
            ].map((t,i)=>(
              <div key={i} style={{ ...kpi, border:`1px solid ${t.color}22` }}>
                <div style={{ color:'#555', fontSize:'9px', letterSpacing:'1px', marginBottom:'4px' }}>{t.label}</div>
                <div style={{ color:t.color, fontSize:'16px', fontWeight:900 }}>{t.val}</div>
                <div style={{ color:'#555', fontSize:'10px' }}>{t.rpm}</div>
              </div>
            ))}
          </div>

          {/* Cost breakdown + market */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'16px' }}>
            <div style={kpi}>
              <div style={sectionLabel}>COST BREAKDOWN</div>
              <div style={{ color:'#aaa', fontSize:'11px', lineHeight:'1.8' }}>
                <div>Fuel: <strong style={{color:'#fff'}}>${analysis.fuelCost.toFixed(0)}</strong></div>
                <div>Driver Pay: <strong style={{color:'#fff'}}>${analysis.driverPay.toFixed(0)}</strong></div>
                <div>Total Cost: <strong style={{color:'#fff'}}>${(analysis.fuelCost+analysis.driverPay).toFixed(0)}</strong></div>
              </div>
            </div>
            <div style={kpi}>
              <div style={sectionLabel}>MARKET BENCHMARK</div>
              {analysis.datBenchmark ? (
                <div style={{ color:'#aaa', fontSize:'11px', lineHeight:'1.8' }}>
                  <div>DAT Avg: <strong style={{color:'#FFD700'}}>${analysis.datBenchmark.dat_avg_rpm}/mi</strong></div>
                  <div>DAT High: <strong style={{color:'#32CD32'}}>${analysis.datBenchmark.dat_high_rpm}/mi</strong></div>
                  <div>DAT Low: <strong style={{color:'#ff4444'}}>${analysis.datBenchmark.dat_low_rpm}/mi</strong></div>
                </div>
              ) : (
                <div style={{ color:'#444', fontSize:'11px' }}>DAT market data not available.<br/>Add DAT_API_KEY to enable live pricing.</div>
              )}
            </div>
          </div>

          {/* Counter offer */}
          {analysis.decision !== 'ACCEPT' && (
            <div style={{ background:'#0a1a0a', border:`1px solid ${dc}`, padding:'12px 16px', marginBottom:'16px' }}>
              <div style={sectionLabel}>COUNTER OFFER</div>
              <div style={{ color:dc, fontSize:'20px', fontWeight:900 }}>${analysis.counterOffer.toFixed(0)} <span style={{fontSize:'13px',fontWeight:400}}>({`$${analysis.counterRpm.toFixed(2)}/mi`})</span></div>
            </div>
          )}

          {/* Talking Points */}
          <div style={{ background:'#0a0a0a', border:'1px solid #1e3a1e', padding:'12px 16px', marginBottom:'16px' }}>
            <div style={sectionLabel}>TALKING POINTS — SAY THIS TO THE BROKER</div>
            {analysis.talkingPoints.map((p,i)=>(
              <div key={i} style={{ display:'flex', gap:'10px', marginBottom:'8px', fontSize:'12px', lineHeight:'1.5' }}>
                <span style={{ color:'#32CD32', fontWeight:700, flexShrink:0 }}>{i+1}.</span>
                <span style={{ color:'#ccc' }}>"{p}"</span>
              </div>
            ))}
          </div>

          {/* Lane History */}
          {analysis.laneHistory.length > 0 && (
            <div>
              <div style={sectionLabel}>LANE HISTORY — LAST {analysis.laneHistory.length} LOADS ON THIS LANE{analysis.laneAvgRpm ? ` | AVG $${analysis.laneAvgRpm.toFixed(2)}/mi` : ''}</div>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'11px' }}>
                <thead><tr>
                  {['DATE','LOAD #','CUSTOMER','RATE','RPM','MARGIN'].map(h=>(
                    <th key={h} style={{ padding:'4px 8px', background:'#050505', color:'#555', textAlign:'left', borderBottom:'1px solid #1a1a1a', fontSize:'9px', letterSpacing:'1px' }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {analysis.laneHistory.map((l,i)=>(
                    <tr key={i} style={{ borderBottom:'1px solid #0a0a0a' }}>
                      <td style={{ padding:'6px 8px', color:'#555' }}>{l.delivery_date||'—'}</td>
                      <td style={{ padding:'6px 8px', color:'#32CD32' }}>{l.load_number||'—'}</td>
                      <td style={{ padding:'6px 8px', color:'#aaa' }}>{l.customer_name||'—'}</td>
                      <td style={{ padding:'6px 8px', color:'#fff', fontWeight:700 }}>${Number(l.rate_total||0).toLocaleString()}</td>
                      <td style={{ padding:'6px 8px', color:'#FFD700' }}>{l.rpm?`$${Number(l.rpm).toFixed(2)}`:'—'}</td>
                      <td style={{ padding:'6px 8px', color:Number(l.profit_margin_percent||0)>=25?'#32CD32':Number(l.profit_margin_percent||0)>=10?'#FFD700':'#ff4444' }}>{l.profit_margin_percent!=null?`${Number(l.profit_margin_percent).toFixed(1)}%`:'—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ display:'flex', justifyContent:'flex-end', marginTop:'12px' }}>
            <button onClick={runAnalysis} style={{ background:'transparent', border:'1px solid #32CD32', color:'#32CD32', padding:'5px 14px', fontFamily:'monospace', fontSize:'10px', cursor:'pointer', letterSpacing:'1px' }}>↻ REFRESH ANALYSIS</button>
          </div>
        </div>
      )}
    </div>
  );
}
